import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    Button,
    Chip,
    IconButton,
    Tooltip,
    Skeleton,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Grid,
    Collapse
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import api from '../../services/api';

interface CourseToReview {
    _id: string;
    title: string;
    description?: string;
    imageUrl?: string;
    old_wordPressId?: number;
    reviewReason: string;
    status: string;
    createdAt: string;
    updatedAt: string;
}

const CourseReviewPage = () => {
    const [courses, setCourses] = useState<CourseToReview[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [expandedReasons, setExpandedReasons] = useState<Set<string>>(new Set());
    const [confirmDialog, setConfirmDialog] = useState<{ open: boolean; courseId: string | null; courseTitle: string }>({
        open: false,
        courseId: null,
        courseTitle: ''
    });

    const REASON_MAX_LENGTH = 150;

    useEffect(() => {
        fetchCoursesToReview();
    }, []);

    const fetchCoursesToReview = async () => {
        try {
            setLoading(true);
            const res = await api.get('/api/course/needs-review');
            setCourses(res.data);
            setError(null);
        } catch (err) {
            console.error('Error fetching courses to review:', err);
            setError('Impossible de charger les cours à reviewer.');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenCourse = (courseId: string) => {
        window.open(`/courses/${courseId}`, '_blank');
    };

    const handleOpenWordPress = (wpId: number) => {
        window.open(`https://edu-jalm.nebulia.tech/wp-admin/admin.php?page=masteriyo#/courses/${wpId}/edit`, '_blank');
    };

    const handleMarkAsReviewed = async () => {
        if (!confirmDialog.courseId) return;

        try {
            await api.put(`/api/course/mark-reviewed/${confirmDialog.courseId}`);
            setCourses(prev => prev.filter(c => c._id !== confirmDialog.courseId));
            setConfirmDialog({ open: false, courseId: null, courseTitle: '' });
        } catch (err) {
            console.error('Error marking course as reviewed:', err);
        }
    };

    const toggleReasonExpand = (courseId: string) => {
        setExpandedReasons(prev => {
            const newSet = new Set(prev);
            if (newSet.has(courseId)) {
                newSet.delete(courseId);
            } else {
                newSet.add(courseId);
            }
            return newSet;
        });
    };

    const formatDate = (dateStr: string) => {
        return new Date(dateStr).toLocaleDateString('fr-FR', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const isReasonLong = (reason: string) => reason && reason.length > REASON_MAX_LENGTH;

    return (
        <Box sx={{ p: 4 }}>
            {/* Header */}
            <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
                <WarningIcon sx={{ fontSize: 40, color: '#f59e0b' }} />
                <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700, fontFamily: 'Quicksand, sans-serif', color: '#1a237e' }}>
                        Cours à Réviser
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ fontFamily: 'Quicksand, sans-serif' }}>
                        {courses.length} cours nécessitent une révision manuelle
                    </Typography>
                </Box>
            </Box>

            {/* Error */}
            {error && (
                <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                    {error}
                </Alert>
            )}

            {/* Loading */}
            {loading && (
                <Grid container spacing={3}>
                    {[1, 2, 3].map((i) => (
                        <Grid size={{ xs: 12, md: 6, lg: 4 }} key={i}>
                            <Paper sx={{ p: 3, borderRadius: 3 }}>
                                <Skeleton variant="text" width="70%" height={32} />
                                <Skeleton variant="text" width="40%" height={24} sx={{ mb: 2 }} />
                                <Skeleton variant="rectangular" height={60} sx={{ borderRadius: 2, mb: 2 }} />
                                <Skeleton variant="rectangular" height={36} sx={{ borderRadius: 2 }} />
                            </Paper>
                        </Grid>
                    ))}
                </Grid>
            )}

            {/* Empty State */}
            {!loading && courses.length === 0 && !error && (
                <Paper sx={{ p: 6, textAlign: 'center', borderRadius: 4, bgcolor: '#f0fdf4', border: '2px solid #10b981' }}>
                    <CheckCircleIcon sx={{ fontSize: 60, color: '#10b981', mb: 2 }} />
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#15803d', fontFamily: 'Quicksand, sans-serif' }}>
                        Tous les cours ont été révisés !
                    </Typography>
                    <Typography color="text.secondary" sx={{ fontFamily: 'Quicksand, sans-serif' }}>
                        Aucun cours n'est en attente de révision.
                    </Typography>
                </Paper>
            )}

            {/* Courses Cards Grid */}
            {!loading && courses.length > 0 && (
                <Grid container spacing={3}>
                    {courses.map((course) => {
                        const isExpanded = expandedReasons.has(course._id);
                        const reasonIsLong = isReasonLong(course.reviewReason);
                        const displayReason = reasonIsLong && !isExpanded
                            ? course.reviewReason.substring(0, REASON_MAX_LENGTH) + '...'
                            : course.reviewReason;

                        return (
                            <Grid size={{ xs: 12, md: 6, lg: 4 }} key={course._id}>
                                <Paper
                                    elevation={0}
                                    sx={{
                                        p: 3,
                                        borderRadius: 4,
                                        border: '1px solid rgba(0,0,0,0.08)',
                                        height: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        transition: 'all 0.3s ease',
                                        '&:hover': {
                                            boxShadow: '0 8px 24px rgba(0,0,0,0.1)',
                                            transform: 'translateY(-4px)',
                                            borderColor: '#f59e0b'
                                        }
                                    }}
                                >
                                    {/* Header */}
                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography
                                                variant="h6"
                                                sx={{
                                                    fontWeight: 700,
                                                    fontFamily: 'Quicksand, sans-serif',
                                                    color: '#1a237e',
                                                    mb: 0.5,
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    display: '-webkit-box',
                                                    WebkitLineClamp: 2,
                                                    WebkitBoxOrient: 'vertical'
                                                }}
                                            >
                                                {course.title}
                                            </Typography>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                <Chip
                                                    label={course.status}
                                                    size="small"
                                                    color={course.status === 'publish' ? 'success' : course.status === 'draft' ? 'warning' : 'default'}
                                                    sx={{ fontSize: '0.7rem' }}
                                                />
                                                {course.old_wordPressId && (
                                                    <Chip
                                                        label="WordPress"
                                                        size="small"
                                                        sx={{ bgcolor: '#21759b', color: 'white', fontSize: '0.7rem' }}
                                                    />
                                                )}
                                            </Box>
                                        </Box>
                                        <WarningIcon sx={{ color: '#f59e0b', fontSize: 28, flexShrink: 0, ml: 1 }} />
                                    </Box>

                                    {/* Reason */}
                                    <Box
                                        sx={{
                                            flex: 1,
                                            mb: 2,
                                            p: 2,
                                            bgcolor: 'rgba(245, 158, 11, 0.08)',
                                            borderRadius: 2,
                                            border: '1px solid rgba(245, 158, 11, 0.2)'
                                        }}
                                    >
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                fontWeight: 700,
                                                color: '#b45309',
                                                textTransform: 'uppercase',
                                                display: 'block',
                                                mb: 1
                                            }}
                                        >
                                            Raison de révision
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: '#92400e',
                                                fontFamily: 'Quicksand, sans-serif',
                                                lineHeight: 1.6,
                                                whiteSpace: 'pre-wrap'
                                            }}
                                        >
                                            {displayReason || 'Non spécifié'}
                                        </Typography>
                                        {reasonIsLong && (
                                            <Button
                                                size="small"
                                                onClick={() => toggleReasonExpand(course._id)}
                                                endIcon={isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                                sx={{
                                                    mt: 1,
                                                    textTransform: 'none',
                                                    color: '#b45309',
                                                    fontWeight: 600,
                                                    p: 0,
                                                    minWidth: 'auto',
                                                    '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' }
                                                }}
                                            >
                                                {isExpanded ? 'Voir moins' : 'Voir plus'}
                                            </Button>
                                        )}
                                    </Box>

                                    {/* Date */}
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2, color: 'text.secondary' }}>
                                        <AccessTimeIcon sx={{ fontSize: 16 }} />
                                        <Typography variant="caption">
                                            Modifié le {formatDate(course.updatedAt)}
                                        </Typography>
                                    </Box>

                                    {/* Actions */}
                                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                        <Button
                                            variant="contained"
                                            size="small"
                                            startIcon={<EditIcon />}
                                            onClick={() => handleOpenCourse(course._id)}
                                            sx={{
                                                flex: 1,
                                                textTransform: 'none',
                                                fontWeight: 600,
                                                borderRadius: 2,
                                                background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)',
                                            }}
                                        >
                                            Review & Modifier
                                        </Button>

                                        {course.old_wordPressId && (
                                            <Tooltip title="Voir sur WordPress">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleOpenWordPress(course.old_wordPressId!)}
                                                    sx={{
                                                        bgcolor: 'rgba(33, 117, 155, 0.1)',
                                                        color: '#21759b',
                                                        '&:hover': { bgcolor: 'rgba(33, 117, 155, 0.2)' }
                                                    }}
                                                >
                                                    <OpenInNewIcon />
                                                </IconButton>
                                            </Tooltip>
                                        )}

                                        <Tooltip title="Marquer comme révisé">
                                            <IconButton
                                                size="small"
                                                onClick={() => setConfirmDialog({ open: true, courseId: course._id, courseTitle: course.title })}
                                                sx={{
                                                    bgcolor: 'rgba(16, 185, 129, 0.1)',
                                                    color: '#10b981',
                                                    '&:hover': { bgcolor: 'rgba(16, 185, 129, 0.2)' }
                                                }}
                                            >
                                                <CheckCircleIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                </Paper>
                            </Grid>
                        );
                    })}
                </Grid>
            )}

            {/* Confirm Dialog */}
            <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog({ open: false, courseId: null, courseTitle: '' })}>
                <DialogTitle sx={{ fontFamily: 'Quicksand, sans-serif', fontWeight: 700 }}>
                    Confirmer la révision
                </DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ fontFamily: 'Quicksand, sans-serif' }}>
                        Voulez-vous marquer le cours "{confirmDialog.courseTitle}" comme révisé ?
                        Cette action supprimera le flag de révision.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmDialog({ open: false, courseId: null, courseTitle: '' })}>
                        Annuler
                    </Button>
                    <Button onClick={handleMarkAsReviewed} variant="contained" color="success">
                        Confirmer
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default CourseReviewPage;
